import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {getUserLoggedIn, updateUser} from "../services/UserService";
import Loader from "../componenets/Loader";
import {useMessageContext} from "../contexts/MessageContext";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useAuth} from "../contexts/AuthContext";
import {useCompany} from "../contexts/CompanyContext";
import CompanyBrandingSection from "../componenets/company/CompanyBrandingSection";
import CompanySettingsSection from "../componenets/company/CompanySettingsSection";
import ApiKeysSection from "../componenets/settings/ApiKeysSection";
import PhoneInput, {formatPhoneForDisplay} from "../componenets/shared/PhoneInput";
import {t} from "../componenets/dashboard/coach/coachTokens";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: t.rs,
    backgroundColor: t.white,
    fontSize: "14px",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" }
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border }
};

const headCellSx = {
  padding: "10px 14px",
  background: t.bg,
  borderBottom: `1px solid ${t.border}`,
  color: t.t3,
  fontSize: "10.5px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".06em",
};

const SettingPage = () => {
  const [currentUser, setCurrentUser] = useState();
  const [orginalCopy, setOriginalCopy] = useState();
  const [showName, setShowName] = useState(false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const {notificationHandler} = useMessageContext();
  const [reload, setReload] = useState(false);
  const [brandingEditOpen, setBrandingEditOpen] = useState(false);

  const {isCompanyAdmin, isSuperAdmin} = useAuth();
  const {company, updateCompanies} = useCompany();

  const shouldShowBrandingSection = isCompanyAdmin() && !isSuperAdmin();
  const shouldShowIntegrations = isCompanyAdmin() || isSuperAdmin();

  useEffect(() => {
    setLoading(true);
    setShowName(false);
    setShowEmail(false);
    setShowPassword(false);
    setShowPhoneNumber(false);

    getUserLoggedIn()
        .then((res) => {
          setCurrentUser(res.data.body);
          setOriginalCopy(res.data.body);
          setLoading(false);
        }).catch(e => {
      notificationHandler.error(getErrorMessage(e.response.data?.errorCode))
      setLoading(false);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const updateName = (name) => {
    setCurrentUser(prv => ({...prv, name: name}))
  }

  const updatePhoneNumber = (phoneNumber) => {
    setCurrentUser(prv => ({...prv, phoneNumber: phoneNumber}))
  }

  const updateEmail = (email) => {
    setCurrentUser(prv => ({...prv, email: email}))
  }

  const updatePassword = (password) => {
    setCurrentUser(prv => ({...prv, password: password}))
  }

  const saveName = () => {
    updateUser({name: currentUser.name})
        .then(() => notificationHandler.success('משתמש עודכן בהצלחה'))
        .then(() => setReload(prv => !prv))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
        })
  }

  const savePhoneNumber = () => {
    updateUser({phoneNumber: currentUser.phoneNumber})
        .then(() => setReload(prv => !prv))
        .then(() => notificationHandler.success('משתמש עודכן בהצלחה'))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
        })
  }

  const saveEmail = () => {
    updateUser({email: currentUser.email})
        .then(() => notificationHandler.success('משתמש עודכן בהצלחה'))
        .then(() => setReload(prv => !prv))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
        })
  }

  const savePassword = () => {
    updateUser({password: currentUser.password})
        .then(() => notificationHandler.success('משתמש עודכן בהצלחה'))
        .then(() => setReload(prv => !prv))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
        })
  }

  const cancel = () => {
    setCurrentUser({...orginalCopy});
    setShowName(false);
    setShowPhoneNumber(false);
    setShowEmail(false);
    setShowPassword(false);
  }

  const settingsRows = [
    {
      icon: <PersonIcon />,
      label: "שם מלא",
      value: currentUser?.name,
      isEditing: showName,
      setEditing: setShowName,
      updateValue: updateName,
      save: saveName
    },
    {
      icon: <PhoneIcon />,
      label: "טלפון",
      value: currentUser?.phoneNumber,
      isEditing: showPhoneNumber,
      setEditing: setShowPhoneNumber,
      updateValue: updatePhoneNumber,
      save: savePhoneNumber,
      isPhone: true
    },
    {
      icon: <EmailIcon />,
      label: "מייל",
      value: currentUser?.email,
      isEditing: showEmail,
      setEditing: setShowEmail,
      updateValue: updateEmail,
      save: saveEmail
    },
    {
      icon: <LockIcon />,
      label: "סיסמה",
      value: currentUser?.password,
      displayValue: "****",
      isEditing: showPassword,
      setEditing: setShowPassword,
      updateValue: updatePassword,
      save: savePassword,
      isPassword: true
    }
  ];

  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        <Loader loading={loading}/>

        {/* ─── Title (on canvas) ──────────────────────────── */}
        <Box sx={{display: "flex", alignItems: "center", gap: 1.25, direction: "rtl"}}>
          <SettingsIcon sx={{color: t.t2, fontSize: "26px"}}/>
          <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
            הגדרות
          </Typography>
        </Box>

        {/* ─── User settings card ─────────────────────────── */}
        <Paper
            sx={{
              backgroundColor: t.white,
              border: `1px solid ${t.border}`,
              borderRadius: t.r,
              boxShadow: t.sh,
              direction: "rtl",
              overflow: "hidden"
            }}
            elevation={0}
        >
          {/* Card Header band */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.25,
            padding: "14px 18px", backgroundColor: t.bg, borderBottom: `1px solid ${t.border}`,
          }}>
            <Box sx={{width: 36, height: 36, borderRadius: "10px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
              <PersonIcon sx={{color: t.t2, fontSize: 20}}/>
            </Box>
            <Box>
              <Typography sx={{fontWeight: 800, fontSize: 15, color: t.text, letterSpacing: "-0.2px"}}>הגדרות משתמש</Typography>
              <Typography sx={{fontSize: 11.5, color: t.t3, fontWeight: 600}}>פרטי החשבון שלך</Typography>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{...headCellSx, width: "22%"}}>סוג</TableCell>
                  <TableCell align="right" sx={headCellSx}>הגדרה</TableCell>
                  <TableCell align="center" sx={{...headCellSx, width: "140px"}}>פעולות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settingsRows.map((row, index) => (
                    <TableRow
                        key={index}
                        sx={{"&:hover td": {backgroundColor: t.greenLight}, transition: "background .12s ease"}}
                    >
                      <TableCell align="right" sx={{padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                          <Box sx={{color: t.t2, display: "flex"}}>
                            {row.icon}
                          </Box>
                          <Typography sx={{fontSize: "13.5px", fontWeight: 600, color: t.text}}>
                            {row.label}:
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`}}>
                        {!row.isEditing ? (
                            <Typography sx={{
                              fontSize: "13.5px",
                              color: t.text,
                              wordBreak: "break-word",
                              ...(row.isPhone && {direction: "ltr", textAlign: "right"})
                            }}>
                              {row.isPhone ? (formatPhoneForDisplay(row.value) || "אין מידע") : (row.displayValue || row.value || "אין מידע")}
                            </Typography>
                        ) : row.isPhone ? (
                            <PhoneInput
                                value={row.value || ""}
                                onChange={(value) => row.updateValue(value)}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                type={row.isPassword ? "password" : "text"}
                                placeholder={row.isPassword ? "****" : ""}
                                value={row.value || ""}
                                onChange={(e) => row.updateValue(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={inputSx}
                            />
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`}}>
                        {!row.isEditing ? (
                            <Tooltip title={`עריכת ${row.label}`}>
                              <IconButton onClick={() => row.setEditing(true)} size="small"
                                          sx={{color: t.t2, "&:hover": {backgroundColor: t.greenLight, color: t.green}}}>
                                <EditIcon fontSize="small"/>
                              </IconButton>
                            </Tooltip>
                        ) : (
                            <Box sx={{display: "flex", justifyContent: "center", gap: 0.5}}>
                              <Tooltip title="שמירה">
                                <IconButton onClick={row.save} size="small"
                                            sx={{color: t.green, "&:hover": {backgroundColor: t.greenLight}}}>
                                  <SaveIcon fontSize="small"/>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="ביטול">
                                <IconButton onClick={cancel} size="small"
                                            sx={{color: t.red, "&:hover": {backgroundColor: t.redLight}}}>
                                  <CancelIcon fontSize="small"/>
                                </IconButton>
                              </Tooltip>
                            </Box>
                        )}
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Branding Section - Only for Company Admins (not Super Admins) */}
        {shouldShowBrandingSection && company && (
            <CompanyBrandingSection
                company={company}
                setLoadCompany={updateCompanies}
                editOpen={brandingEditOpen}
                setEditOpen={setBrandingEditOpen}
            />
        )}

        {/* Company Settings Section - Only for Company Admins (not Super Admins) */}
        {shouldShowBrandingSection && company && (
            <CompanySettingsSection
                company={company}
                setLoadCompany={updateCompanies}
            />
        )}

        {/* API Keys — Company admin or Super admin */}
        {shouldShowIntegrations && <ApiKeysSection/>}
      </Box>
  );
};

export default SettingPage;
